import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  Shield,
  TrendingUp,
  Users,
  FileCheck,
  Home,
  Building2,
  ChevronRight,
  Star,
  Briefcase,
  DollarSign,
  Clock,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1776500588108-1e059459f14c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBob3VzZSUyMGV4dGVyaW9yJTIwcmVhbCUyMGVzdGF0ZXxlbnwwfHx8fDE3NzgyMTEyNjV8MA&ixlib=rb-4.1.0&q=85";
const AERIAL_IMG =
  "https://images.unsplash.com/photo-1770938474431-d1192cac9642?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3VzZSUyMHJlYWwlMjBlc3RhdGUlMjBhZXJpYWwlMjBuZWlnaGJvcmhvb2R8ZW58MHx8fHwxNzc4MjExMjcxfDA&ixlib=rb-4.1.0&q=85";
const RENO_IMG =
  "https://images.unsplash.com/photo-1753977725475-41b221add2c0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxob3VzZSUyMHJlbm92YXRpb24lMjBiZWZvcmUlMjBhZnRlciUyMHJlYWwlMjBlc3RhdGUlMjBpbnZlc3RtZW50fGVufDB8fHx8MTc3ODIxMTI2NXww&ixlib=rb-4.1.0&q=85";

const HERO_IMAGES = [
  {
    src: HERO_IMG,
    title: "Luxury Modern Homes",
    subtitle:
      "Discover high-value real estate opportunities built for cash flow.",
  },
  {
    src: "https://images.unsplash.com/photo-1494526585095-c41746248156?auto=format&fit=crop&w=2000&q=50",
    title: "Prime Neighborhood Insights",
    subtitle: "Invest smarter with data-driven location intelligence.",
  },
  {
    src: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=2000&q=50",
    title: "Renovation & ROI Deals",
    subtitle: "Unlock value through strategic property improvements.",
  },
];

const LandingPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const isLoggedIn = !loading && user;

  const [activeHero, setActiveHero] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveHero((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden">
      {/* ──── NAV ──── */}
      <nav
        className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-lg border-b border-gray-100"
        data-testid="landing-nav"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link
            to="/"
            className="flex items-center gap-2 sm:gap-2.5 min-w-0"
            data-testid="landing-logo"
          >
            <img src="/logo.svg" alt="Cashflow4u" className="w-8 h-8 sm:w-9 sm:h-9 shrink-0" />
            <span className="text-lg sm:text-xl font-bold tracking-tight truncate">
              <span style={{ color: "#084F9B" }}>Cash</span>
              <span style={{ color: "#F8B105" }}>flow</span>
              <span style={{ color: "#084F9B" }}>4u</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-gray-600">
            <a
              href="#how-it-works"
              className="hover:text-gray-900 transition-colors"
            >
              How It Works
            </a>
            <a
              href="#who-we-serve"
              className="hover:text-gray-900 transition-colors"
            >
              Who We Serve
            </a>
            <a href="#why-us" className="hover:text-gray-900 transition-colors">
              Why Cashflow4u
            </a>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {loading ? (
              <div className="w-24 h-9" />
            ) : isLoggedIn ? (
              <Button
                onClick={() => navigate("/dashboard")}
                style={{ backgroundColor: "#084F9B" }}
                className="hover:opacity-90 text-white text-xs sm:text-sm px-3 sm:px-4"
                data-testid="landing-dashboard-btn"
              >
                <span className="hidden sm:inline">Go to Dashboard</span>
                <span className="sm:hidden">Dashboard</span>
                <ArrowRight className="w-4 h-4 ml-1.5 sm:ml-2" />
              </Button>
            ) : (
              <>
                <Link to="/login">
                  <Button
                    variant="ghost"
                    className="text-gray-700 hover:text-gray-900 text-xs sm:text-sm px-2 sm:px-4"
                    data-testid="landing-login-btn"
                  >
                    Log In
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button
                    style={{ backgroundColor: "#084F9B" }}
                    className="hover:opacity-90 text-white text-xs sm:text-sm px-3 sm:px-4"
                    data-testid="landing-signup-btn"
                  >
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-1.5 sm:ml-2 hidden sm:inline-block" />
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ──── HERO ──── */}

      <section className="relative pt-16" data-testid="hero-section">
        <div className="relative min-h-[85vh] sm:min-h-[92vh] flex items-center overflow-hidden">
          {/* Carousel Background */}
          {HERO_IMAGES.map((item, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === activeHero ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={item.src}
                alt={item.title}
                className="w-full h-full object-cover object-[center_30%]"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/80 to-gray-900/40 sm:from-gray-900/90 sm:via-gray-900/70 sm:to-gray-900/30" />
            </div>
          ))}

          {/* Content */}
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full py-12 sm:py-0">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white/90 text-xs sm:text-sm mb-6 sm:mb-8">
                <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                Live deals available now
              </div>

              <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight">
                {HERO_IMAGES[activeHero].title}
              </h1>

              <p className="mt-4 sm:mt-6 text-base sm:text-lg text-white/80 leading-relaxed max-w-xl">
                {HERO_IMAGES[activeHero].subtitle}
              </p>

              <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
                {isLoggedIn ? (
                  <Button
                    size="lg"
                    onClick={() => navigate("/dashboard")}
                    style={{ backgroundColor: "#F8B105", color: "#1a1a1a" }}
                    className="hover:opacity-90 text-base px-6 sm:px-8 h-12 sm:h-14 font-semibold w-full sm:w-auto"
                  >
                    Go to Dashboard
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                ) : (
                  <>
                    <Link to="/signup" className="w-full sm:w-auto">
                      <Button
                        size="lg"
                        style={{ backgroundColor: "#F8B105", color: "#1a1a1a" }}
                        className="hover:opacity-90 text-base px-6 sm:px-8 h-12 sm:h-14 font-semibold w-full sm:w-auto"
                      >
                        Create Free Account
                        <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>

                    <Link to="/login" className="w-full sm:w-auto">
                      <Button
                        size="lg"
                        variant="outline"
                        className="border-white/30 text-white hover:bg-white/10 text-base px-6 sm:px-8 h-12 sm:h-14 w-full sm:w-auto"
                      >
                        Sign In
                      </Button>
                    </Link>
                  </>
                )}
              </div>

              {/* Dots */}
              <div className="flex gap-2 mt-8 sm:mt-12">
                {HERO_IMAGES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveHero(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === activeHero ? "w-8 bg-white" : "w-4 bg-white/40"
                    }`}
                  />
                ))}
              </div>

              {/* Trust bar */}
              <div className="mt-10 sm:mt-14 flex flex-wrap items-center gap-x-6 gap-y-3 sm:gap-8 text-white/60 text-xs sm:text-sm">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  <span>NDA Protected</span>
                </div>
                <div className="flex items-center gap-2">
                  <FileCheck className="w-4 h-4" />
                  <span>Digital Contracts</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Fast Closings</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ──── STATS BAR ──── */}
      <section className="bg-gray-50 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-12 grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
          {[
            { value: "500+", label: "Active Investors" },
            { value: "$50M+", label: "Deal Volume" },
            { value: "98%", label: "Close Rate" },
            { value: "15", label: "States Covered" },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p
                className="text-2xl sm:text-3xl lg:text-4xl font-bold"
                style={{ color: "#084F9B" }}
              >
                {stat.value}
              </p>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ──── HOW IT WORKS ──── */}
      <section
        id="how-it-works"
        className="py-16 sm:py-24 bg-white"
        data-testid="how-it-works-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <p
              className="text-xs sm:text-sm font-semibold uppercase tracking-widest mb-3"
              style={{ color: "#F8B105" }}
            >
              How It Works
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
              From Submission to Closing
            </h2>
            <p className="mt-4 text-gray-500 max-w-2xl mx-auto text-sm sm:text-base">
              A streamlined process that keeps everyone in sync — agents submit,
              deals get verified, investors close.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                step: "01",
                icon: Home,
                title: "Submit a Deal",
                desc: "Agents and sellers list properties with details, photos, and financials. Save drafts and submit when ready.",
              },
              {
                step: "02",
                icon: Shield,
                title: "Review & Approve",
                desc: "Every deal is reviewed and verified — financials checked, details confirmed — before going live on the marketplace.",
              },
              {
                step: "03",
                icon: TrendingUp,
                title: "Investors Close",
                desc: "Investors sign NDAs, reserve deals, complete document checklists, and close — all tracked in one place.",
              },
            ].map((item) => (
              <div
                key={item.step}
                className="group relative p-6 sm:p-8 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-lg transition-all duration-300"
              >
                <span className="text-5xl sm:text-6xl font-black text-gray-100 group-hover:text-blue-50 absolute top-4 right-4 sm:top-6 sm:right-6 transition-colors">
                  {item.step}
                </span>
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ backgroundColor: "rgba(8, 79, 155, 0.08)" }}
                >
                  <item.icon className="w-6 h-6" style={{ color: "#084F9B" }} />
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm sm:text-base text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──── WHO WE SERVE ──── */}
      <section
        id="who-we-serve"
        className="py-16 sm:py-24 bg-gray-50"
        data-testid="who-we-serve-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <p
                className="text-xs sm:text-sm font-semibold uppercase tracking-widest mb-3"
                style={{ color: "#F8B105" }}
              >
                Who We Serve
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                Built for Every Side of the Deal
              </h2>
              <p className="text-gray-500 mb-8 sm:mb-10 text-sm sm:text-base leading-relaxed">
                Whether you're sourcing properties, selling assets, or investing
                capital — Cashflow4u gives you the tools and visibility to move
                faster.
              </p>

              <div className="space-y-5 sm:space-y-6">
                {[
                  {
                    icon: Briefcase,
                    title: "Agents",
                    desc: "Submit deals, track review status, manage drafts, and get feedback on your listings in real-time.",
                  },
                  {
                    icon: Home,
                    title: "Sellers",
                    desc: "List your properties with photos, documents, and financials. Reach qualified investors fast.",
                  },
                  {
                    icon: Building2,
                    title: "Wholesalers",
                    desc: "Source off-market deals, upload contracts, and connect with cash-ready buyers on the platform.",
                  },
                  {
                    icon: DollarSign,
                    title: "Investors",
                    desc: "Browse live deals, sign NDAs, reserve properties, and complete closing documents — all digitally.",
                  },
                ].map((role) => (
                  <div key={role.title} className="flex gap-4">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                      style={{ backgroundColor: "rgba(248, 177, 5, 0.1)" }}
                    >
                      <role.icon
                        className="w-5 h-5"
                        style={{ color: "#F8B105" }}
                      />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {role.title}
                      </h4>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {role.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={AERIAL_IMG}
                  alt="Neighborhood aerial view"
                  className="w-full h-[520px] object-cover"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-5 border border-gray-100 max-w-xs">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: "rgba(8, 79, 155, 0.1)" }}
                  >
                    <TrendingUp
                      className="w-5 h-5"
                      style={{ color: "#084F9B" }}
                    />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      Deal Approved
                    </p>
                    <p className="text-xs text-gray-500">
                      456 Oak Ave, Dallas TX
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-500">
                    Asking:{" "}
                    <span className="font-semibold text-gray-900">
                      $200,000
                    </span>
                  </span>
                  <span className="text-green-600 font-semibold">
                    ARV: $300,000
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ──── WHY US ──── */}
      <section
        id="why-us"
        className="py-16 sm:py-24 bg-white"
        data-testid="why-us-section"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <p
              className="text-xs sm:text-sm font-semibold uppercase tracking-widest mb-3"
              style={{ color: "#F8B105" }}
            >
              Why Cashflow4u
            </p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
              The Tools That Close Deals
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                icon: FileCheck,
                title: "Digital Document Signing",
                desc: "Multi-step document workflows — NDAs, contracts, disclosures — all signed and tracked online.",
              },
              {
                icon: Shield,
                title: "Role-Based Access",
                desc: "Agents, sellers, wholesalers, and investors each see exactly what they need. Nothing more, nothing less.",
              },
              {
                icon: TrendingUp,
                title: "Financial Analytics",
                desc: "ARV, rehab costs, wholesale fees, and ROI calculated automatically for every deal.",
              },
              {
                icon: Building2,
                title: "Deal Pipeline",
                desc: "Submit, review, approve, reserve, close — the entire lifecycle in one streamlined view.",
              },
              {
                icon: Users,
                title: "Investor Management",
                desc: "Import investors, track capital preferences, assign deals, and send invites at scale.",
              },
              {
                icon: Clock,
                title: "Save Drafts",
                desc: "Start a deal submission now, save as draft, and finish later. Never lose your progress.",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className="p-6 rounded-xl border border-gray-100 hover:shadow-md transition-shadow"
              >
                <feature.icon
                  className="w-8 h-8 mb-4"
                  style={{ color: "#084F9B" }}
                />
                <h3 className="text-lg font-semibold text-gray-900 mb-1.5">
                  {feature.title}
                </h3>
                <p className="text-sm text-gray-500 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ──── CTA ──── */}
      <section
        className="py-16 sm:py-24"
        style={{ backgroundColor: "#084F9B" }}
        data-testid="cta-section"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">
            Ready to Find Your Next Deal?
          </h2>
          <p className="text-white/70 text-base sm:text-lg mb-8 sm:mb-10 max-w-2xl mx-auto">
            Join agents, sellers, and investors already using Cashflow4u to
            close deals faster and smarter.
          </p>

          {isLoggedIn ? (
            <Button
              size="lg"
              onClick={() => navigate("/dashboard")}
              style={{ backgroundColor: "#F8B105", color: "#1a1a1a" }}
              className="hover:opacity-90 text-base px-8 sm:px-10 h-12 sm:h-14 font-semibold w-full sm:w-auto"
              data-testid="cta-dashboard-btn"
            >
              Open Dashboard
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <Link to="/signup" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  style={{ backgroundColor: "#F8B105", color: "#1a1a1a" }}
                  className="hover:opacity-90 text-base px-8 sm:px-10 h-12 sm:h-14 font-semibold w-full"
                  data-testid="cta-signup-btn"
                >
                  Create Free Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/login" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 text-base px-8 sm:px-10 h-12 sm:h-14 w-full"
                  data-testid="cta-login-btn"
                >
                  Sign In
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ──── FOOTER ──── */}
      <footer className="bg-gray-900 text-gray-400 py-10 sm:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            <div className="flex items-center gap-2.5">
              <img src="/logo.svg" alt="Cashflow4u" className="w-8 h-8" />
              <span className="text-lg font-bold">
                <span style={{ color: "#5B9BD5" }}>Cash</span>
                <span style={{ color: "#F8B105" }}>flow</span>
                <span style={{ color: "#5B9BD5" }}>4u</span>
              </span>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 sm:gap-8 text-sm">
              <a
                href="#how-it-works"
                className="hover:text-white transition-colors"
              >
                How It Works
              </a>
              <a
                href="#who-we-serve"
                className="hover:text-white transition-colors"
              >
                Who We Serve
              </a>
              <a href="#why-us" className="hover:text-white transition-colors">
                Features
              </a>
              <Link to="/login" className="hover:text-white transition-colors">
                Log In
              </Link>
            </div>

            <p className="text-xs text-gray-500">
              &copy; {new Date().getFullYear()} Cashflow4u. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
